import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { planService } from '../../application/PlanService';
import { PlanFlat } from '../../domain/plan/PlanFlat';
import { PlanStatus } from '../../domain/plan/PlanStatus';
import eventsPanel from '../../services/events';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

type UseDuplicatePlanProps = {
  sourcePlan: PlanFlat;
  duplicates: { label: string; status: PlanStatus; execution_date: Date }[];
};

type UseDuplicatePlanResponse = ServiceResponse<
  ({ sourcePlan, duplicates }: UseDuplicatePlanProps) => void,
  Array<PlanFlat | null>
>;

export const useDuplicatePlan = (): UseDuplicatePlanResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const queryClient = useQueryClient();

  const [createdDuplicates, setCreatedDuplicates] = useState<Array<PlanFlat | null> | undefined>();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ sourcePlan, duplicates }: UseDuplicatePlanProps) => planService.duplicatePlan({ sourcePlan, duplicates }),
    {
      onSuccess: (duplicates: Array<PlanFlat | null>) => {
        setCreatedDuplicates(duplicates);
        queryClient.invalidateQueries([`planner_search_plans`]);
        queryClient.invalidateQueries([`planner_flat_plans`]);
      },
      onError: (err: any) => {
        console.info(`Duplicate Plan Error:`, err);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        const error = [
          `highway.subscription.error.vehicles_quota_exceeded`,
          `highway.project.error.stops_quota_exceeded`,
        ];
        if (error.includes(err?.response?.data.messageId)) {
          showSnackbarMessage({
            messageIntlId: `infoerror.${err.response.data.messageId}`,
            autoHideDuration: 8000,
          });
          eventsPanel.planDuplicateError(err?.response?.data.messageId);
          return;
        }
        showSnackbarMessage({ messageIntlId: `infoerror.duplicate_plan_error`, autoHideDuration: 8000 });
      },
    }
  );

  return {
    isLoading,
    error: isError,
    success: isSuccess,
    data: createdDuplicates,
    run: mutate,
  };
};
